import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Download,
  ChevronDown,
  Search,
  MoreVertical,
  FileText,
  Archive,
  RotateCcw,
  Trash2,
} from "lucide-react";
import SuiteFormModal from "../components/SuiteFormModal";
import { suiteService } from "../services/suiteService";
import { getStoredClientId } from "../utils/tokenUtils";
import toast from "react-hot-toast";
import { logger } from "../services/logService";
import PageSkeleton from "../components/skeletons/PageSkeleton";

// Utility function to format relative dates
const formatRelativeDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "1 day ago";
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 14) return "1 week ago";
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 60) return "1 month ago";
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
};

const SuiteManagementPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get("project_id");
  const projectName = searchParams.get("project_name") || "";
  const clientId = searchParams.get("client_id") || getStoredClientId();

  const [suites, setSuites] = useState([]);
  const [filteredSuites, setFilteredSuites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuiteModal, setShowSuiteModal] = useState(false);
  const [editingSuite, setEditingSuite] = useState(null);

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortBy, setSortBy] = useState("most_recent");
  const [assignTo, setAssignTo] = useState("all");
  const [selectedSuites, setSelectedSuites] = useState(new Set());

  const loadSuites = useCallback(async () => {
    if (!projectId || !clientId) return;

    try {
      setLoading(true);
      const response = await suiteService.getSuites(projectId, clientId);
      // API: data: { suites: [...], pagination } — suiteService also exposes `suites` at top level
      let suitesList = [];
      if (Array.isArray(response?.suites)) {
        suitesList = response.suites;
      } else if (Array.isArray(response?.data?.suites)) {
        suitesList = response.data.suites;
      } else if (Array.isArray(response?.data)) {
        suitesList = response.data;
      } else if (Array.isArray(response)) {
        suitesList = response;
      }
      setSuites(suitesList);
    } catch (error) {
      console.error("SuiteManagementPage: Error loading suites:", error);
      logger.error(error, "Failed to load suites");
      toast.error("Failed to load suites");
      setSuites([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, clientId]);

  useEffect(() => {
    loadSuites();
  }, [loadSuites]);

  // Filter and sort suites
  useEffect(() => {
    let filtered = [...suites];
    console.log("SuiteManagementPage: Filtering suites. Total:", suites.length);

    // Apply search filter
    if (searchQuery) {
      const beforeSearch = filtered.length;
      filtered = filtered.filter(
        (suite) =>
          suite.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          suite.description?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      console.log(
        "SuiteManagementPage: After search filter:",
        filtered.length,
        "from",
        beforeSearch,
      );
    }

    // Apply status filter
    if (selectedStatus !== "all") {
      // For now, we'll use is_deleted as status indicator
      // You may need to add a status field to your suites table
      const beforeStatus = filtered.length;
      if (selectedStatus === "active") {
        filtered = filtered.filter((suite) => !suite.is_deleted);
      } else if (selectedStatus === "archived") {
        filtered = filtered.filter((suite) => suite.is_deleted);
      }
      console.log(
        "SuiteManagementPage: After status filter:",
        filtered.length,
        "from",
        beforeStatus,
      );
    }

    // Apply assign to filter
    if (assignTo === "me") {
      // Filter by current user - you'll need to get the current user ID
      // filtered = filtered.filter(suite => suite.created_by === currentUserId);
    }

    // Apply sorting
    if (sortBy === "most_recent") {
      filtered.sort(
        (a, b) =>
          new Date(b.updated_at || b.created_at) -
          new Date(a.updated_at || a.created_at),
      );
    } else if (sortBy === "oldest") {
      filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (sortBy === "name_asc") {
      filtered.sort((a, b) => {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else if (sortBy === "name_desc") {
      filtered.sort((a, b) => {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        return nameB.localeCompare(nameA);
      });
    }

    console.log("SuiteManagementPage: Final filtered suites:", filtered.length);
    setFilteredSuites(filtered);
  }, [suites, searchQuery, selectedStatus, sortBy, assignTo]);

  const handleCreateSuite = () => {
    setEditingSuite(null);
    setShowSuiteModal(true);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedSuites(new Set(filteredSuites.map((suite) => suite.suite_id)));
    } else {
      setSelectedSuites(new Set());
    }
  };

  const handleSelectSuite = (suiteId) => {
    const newSelected = new Set(selectedSuites);
    if (newSelected.has(suiteId)) {
      newSelected.delete(suiteId);
    } else {
      newSelected.add(suiteId);
    }
    setSelectedSuites(newSelected);
  };

  const handleEditSuite = (suite) => {
    setEditingSuite(suite);
    setShowSuiteModal(true);
  };

  const handleDeleteSuite = async (suite) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${suite.name}"? This will fail if the suite has test cases.`,
      )
    ) {
      return;
    }

    try {
      await suiteService.deleteSuite(suite.suite_id, projectId, clientId);
      toast.success("Suite deleted successfully!");
      loadSuites();
    } catch (error) {
      logger.error(error, "Failed to delete suite");
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete suite";
      toast.error(errorMessage);
      if (error.response?.data?.code === "SUITE_HAS_TEST_CASES") {
        toast.error(
          "Cannot delete suite with test cases. Remove or move test cases first.",
        );
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSuites.size === 0) return;
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedSuites.size} suite(s)?`,
      )
    ) {
      return;
    }

    try {
      for (const suiteId of selectedSuites) {
        await suiteService.deleteSuite(suiteId, projectId, clientId);
      }
      toast.success(`${selectedSuites.size} suite(s) deleted successfully!`);
      setSelectedSuites(new Set());
      loadSuites();
    } catch (error) {
      logger.error(error, "Failed to delete suites");
      toast.error("Failed to delete some suites");
    }
  };

  const handleBulkArchive = async () => {
    // Placeholder - you'll need to implement archive functionality
    toast("Archive functionality coming soon");
  };

  const handleBulkRestore = async () => {
    // Placeholder - you'll need to implement restore functionality
    toast("Restore functionality coming soon");
  };

  // Get status badge color and text
  const getStatusBadge = (suite) => {
    // For now, using is_deleted as status indicator
    // You may want to add a status field to your suites table
    if (suite.is_deleted) {
      return { text: "Archived", className: "bg-gray-100 text-gray-700" };
    }
    // Default to Active for now
    return { text: "Active", className: "bg-indigo-100 text-indigo-700" };
  };

  // Get test run count (placeholder - you may need to add this to your API)
  const getTestRunCount = (suite) => {
    // This would need to come from your API
    // For now, returning a placeholder
    return suite.test_runs_count || 0;
  };

  if (!projectId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Please select a project to manage suites.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen p-4">
        <PageSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <h1 className="text-3xl font-bold text-gray-900">Test Suites</h1>
      </div>

      <div className="px-6 py-6">
        {/* Main Heading Section */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              Manage Test Suites
            </h2>
            <p className="text-sm text-gray-600">
              Organize and manage multiple test cases by test suite.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCreateSuite}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 font-medium"
            >
              <Plus className="w-4 h-4" />
              Test Suites
            </button>
            <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center gap-2 font-medium">
              <Plus className="w-4 h-4" />
              Test Plan
            </button>
            <div className="relative">
              <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center gap-2 font-medium">
                <Download className="w-4 h-4" />
                Import Test Suites
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Filter and Search Bar */}
        <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200">
          {selectedSuites.size > 0 ? (
            <>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={
                    selectedSuites.size === filteredSuites.length &&
                    filteredSuites.length > 0
                  }
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">All</span>
              </label>
              <span className="px-2.5 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                {selectedSuites.size} Selected
              </span>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1.5 text-sm text-red-600 border border-gray-300 rounded-md hover:bg-red-50 flex items-center gap-1"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
              <button
                onClick={handleBulkArchive}
                className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              >
                <Archive className="w-4 h-4" />
                Archive
              </button>
              <button
                onClick={handleBulkRestore}
                className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              >
                <RotateCcw className="w-4 h-4" />
                Restore
              </button>
            </>
          ) : (
            <>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={
                    selectedSuites.size === filteredSuites.length &&
                    filteredSuites.length > 0
                  }
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">All</span>
              </label>

              <div className="relative">
                <button className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1">
                  Assign To: {assignTo === "all" ? "All" : "Me"}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>

              <div className="relative">
                <button className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1">
                  Sort:{" "}
                  {sortBy === "most_recent"
                    ? "Most Recent"
                    : sortBy === "oldest"
                      ? "Oldest"
                      : "Name"}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>

              <div className="relative">
                <button className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1">
                  Status
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          <div className="flex-1 max-w-md ml-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Q Search test suites..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Test Suites List */}
        {!loading && filteredSuites.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-6">
              <FileText className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              No Test Suites yet
            </h3>
            <p className="text-sm text-gray-600 text-center mb-8 max-w-md">
              Create test suites to organize your tests, add test cases.
            </p>
            <button
              onClick={handleCreateSuite}
              className="px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Test Suites
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSuites.map((suite) => {
              const statusBadge = getStatusBadge(suite);
              const testRunCount = getTestRunCount(suite);
              const testCaseCount = parseInt(suite.test_cases_count || 0, 10);

              return (
                <div
                  key={suite.suite_id}
                  className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedSuites.has(suite.suite_id)}
                    onChange={() => handleSelectSuite(suite.suite_id)}
                    className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                  />

                  {/* Document Icon */}
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded">
                    <FileText className="w-5 h-5 text-gray-600" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3
                      className="font-semibold text-gray-900 mb-1 truncate cursor-pointer hover:text-indigo-600"
                      onClick={() =>
                        navigate(
                          `/test-cases?project_id=${projectId}&suite_id=${suite.suite_id}&project_name=${encodeURIComponent(projectName || "")}`,
                        )
                      }
                    >
                      {suite.name}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {suite.description || "No description"}
                    </p>
                  </div>

                  {/* Right Side Info */}
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500 whitespace-nowrap">
                      {formatRelativeDate(suite.updated_at || suite.created_at)}
                    </span>

                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.className}`}
                    >
                      {statusBadge.text}
                    </span>

                    <button
                      onClick={() =>
                        navigate(
                          `/test-cases?project_id=${projectId}&suite_id=${suite.suite_id}`,
                        )
                      }
                      className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline whitespace-nowrap"
                    >
                      {testCaseCount} Test Cases
                    </button>

                    <button
                      onClick={() =>
                        navigate(
                          `/test-runs?project_id=${projectId}&suite_id=${suite.suite_id}`,
                        )
                      }
                      className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline whitespace-nowrap"
                    >
                      {testRunCount} Test Run{testRunCount !== 1 ? "s" : ""}
                    </button>

                    <div className="relative">
                      <button
                        className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Show dropdown menu with Edit, Clone, Delete options
                        }}
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                      {/* Dropdown menu would go here */}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Suite Form Modal */}
      <SuiteFormModal
        isOpen={showSuiteModal}
        onClose={() => {
          setShowSuiteModal(false);
          setEditingSuite(null);
        }}
        onSubmit={loadSuites}
        initialData={editingSuite}
        projectId={projectId}
        clientId={clientId}
      />
    </div>
  );
};

export default SuiteManagementPage;
